import { create } from 'zustand';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  writeBatch,
  query,
  where,
  updateDoc,
} from 'firebase/firestore';
import { firestore } from '../lib/firebase/config';
import { Dispute, DisputingGuide } from '../types/dispute';
import { Evidence } from '../types/pilgrimGuide';
import { COLLECTIONS } from './pilgrimGuide/constants';

const inferReopenedStatus = (dispute: Dispute): Dispute['status'] =>
  (dispute.disputingGuides || []).some((guide) => guide?.evidence)
    ? 'under_processing'
    : 'pending';

const getParticipantKey = (guide: Pick<DisputingGuide, 'guideId' | 'organizerId'>) =>
  guide.guideId || guide.organizerId || '';

const mapGuideForViewer = (
  guide: DisputingGuide,
  userRole: string | null,
  userId: string | null
): DisputingGuide => {
  if (userRole !== 'organizer') {
    return {
      ...guide,
      hasSubmittedEvidence: Boolean(guide.evidence),
    };
  }

  const isCurrentOrganizer = guide.organizerId === userId;

  return {
    ...guide,
    evidence: undefined,
    hasSubmittedEvidence: isCurrentOrganizer ? Boolean(guide.evidence) : false,
  };
};

type DisputesState = {
  disputes: Record<string, Dispute>;
  loading: boolean;
  error: string | null;
  fetchDisputes: () => Promise<void>;
  submitEvidence: (
    disputeId: string,
    organizerId: string,
    evidence: { text: string; files: Array<{ url: string; type: 'image' | 'pdf'; name: string; }> }
  ) => Promise<void>;
  resolveDispute: (disputeId: string, resolvedOrganizerId: string) => Promise<void>;
  reopenDispute: (disputeId: string) => Promise<void>;
};

export const useDisputesStore = create<DisputesState>((set, get) => ({
  disputes: {},
  loading: false,
  error: null,

  fetchDisputes: async () => {
    set({ loading: true, error: null });
    try {
      const userRole = localStorage.getItem('userRole');
      const userId = localStorage.getItem('userId');

      console.log('Fetching disputes...');
      const querySnapshot = await getDocs(
        collection(firestore, COLLECTIONS.DISPUTES)
      );
      const disputes: Record<string, Dispute> = {};

      // Fetch all guides first
      const guidesSnapshot = await getDocs(collection(firestore, 'Guides'));
      const guidesMap = new Map();
      guidesSnapshot.docs.forEach(doc => {
        guidesMap.set(doc.id, doc.data());
      });

      // Fetch all organizers
      const organizersSnapshot = await getDocs(collection(firestore, 'Organizers'));
      const organizersMap = new Map();
      organizersSnapshot.docs.forEach(doc => {
        organizersMap.set(doc.id, doc.data());
      });

      // Fetch all pilgrims to get package information
      const pilgrimsSnapshot = await getDocs(collection(firestore, 'Pilgrims'));
      const pilgrimsMap = new Map();
      pilgrimsSnapshot.docs.forEach(doc => {
        pilgrimsMap.set(doc.id, doc.data());
      });

      console.log('Data maps created:', {
        guidesCount: guidesMap.size,
        organizersCount: organizersMap.size,
        pilgrimsCount: pilgrimsMap.size
      });

      for (const doc of querySnapshot.docs) {
        const disputeData = doc.data();
        console.log(`Processing dispute ${doc.id}:`, disputeData);

        // Get pilgrim package name
        const pilgrimData = pilgrimsMap.get(disputeData.pilgrimId);
        const packageName = pilgrimData?.package_nickName;

        // Process each guide in the dispute
        const processedGuides = await Promise.all((disputeData.disputingGuides || []).map(async (guide: any) => {
          console.log(`Processing guide in dispute ${doc.id}:`, guide);

          const guideData = guidesMap.get(guide.guideId);
          const organizerId = guide.organizerId || guideData?.organizerId || '';
          const organizerData = organizerId
            ? organizersMap.get(organizerId)
            : null;

          if (!organizerData && organizerId) {
            console.warn(`Organizer ${organizerId} not found for guide ${guide.guideId}`);
          }

          console.log('Guide and organizer data:', {
            guideId: guide.guideId,
            guideName: guideData?.name || guide.guideName,
            organizerId,
            organizerName: organizerData?.name
          });

          return {
            ...guide,
            guideName: guideData?.name || guide.guideName || 'Not specified',
            organizerId,
            organizerName: organizerData?.name || guide.organizerName || 'Not specified'
          };
        }));

        const matchesScope =
          userRole === 'admin' ||
          !userRole ||
          !userId ||
          (userRole === 'organizer' &&
            processedGuides.some((guide) => guide.organizerId === userId)) ||
          (userRole === 'guide' &&
            processedGuides.some((guide) => guide.guideId === userId));

        if (!matchesScope) {
          continue;
        }

        disputes[doc.id] = {
          ...disputeData,
          id: doc.id,
          disputingGuides: processedGuides.map((guide) =>
            mapGuideForViewer(guide, userRole, userId)
          ),
          packageName: packageName // Add package name to dispute
        } as Dispute;
      }

      console.log('Final disputes data:', {
        count: Object.keys(disputes).length,
        disputes: Object.values(disputes).map(d => ({
          id: d.id,
          pilgrimName: d.pilgrimName,
          packageName: d.packageName,
          guides: d.disputingGuides.map(g => ({
            guideName: g.guideName,
            organizerName: g.organizerName
          }))
        }))
      });

      set({ disputes, loading: false });
    } catch (error: any) {
      console.error('Error fetching disputes:', error);
      set({ error: error.message || 'فشل في جلب النزاعات', loading: false });
    }
  },

  submitEvidence: async (disputeId: string, organizerId: string, evidenceData: { text: string; files: Array<{ url: string; type: 'image' | 'pdf'; name: string; }> }) => {
    try {
      const disputeRef = doc(firestore, COLLECTIONS.DISPUTES, disputeId);
      const disputeDoc = await getDoc(disputeRef);

      if (!disputeDoc.exists()) {
        throw new Error('النزاع غير موجود');
      }

      const dispute = disputeDoc.data() as Dispute;
      const now = Date.now();

      const evidence: Evidence = {
        text: evidenceData.text,
        files: evidenceData.files,
        submittedAt: now,
        updatedAt: now,
      };

      const updatedGuides: DisputingGuide[] = dispute.disputingGuides.map(
        (guide) => {
          if ((guide.organizerId || guide.guideId) === organizerId) {
            return {
              ...guide,
              evidence,
            };
          }
          return guide;
        }
      );

      const firestoreUpdates = {
        disputingGuides: updatedGuides,
        status: 'under_processing' as const,
        updatedAt: now,
      };

      const updates = {
        ...firestoreUpdates,
        disputingGuides: updatedGuides.map((guide) =>
          mapGuideForViewer(guide, localStorage.getItem('userRole'), localStorage.getItem('userId'))
        ),
      };

      await updateDoc(disputeRef, firestoreUpdates);

      set((state) => ({
        disputes: {
          ...state.disputes,
          [disputeId]: {
            ...state.disputes[disputeId],
            ...updates,
          },
        },
      }));
    } catch (error: any) {
      console.error('Error submitting evidence:', error);
      throw new Error(error.message || 'فشل في تقديم الإثبات');
    }
  },

  resolveDispute: async (disputeId: string, resolvedOrganizerId: string) => {
    try {
      const batch = writeBatch(firestore);

      // Get dispute details
      const disputeRef = doc(firestore, COLLECTIONS.DISPUTES, disputeId);
      const disputeDoc = await getDoc(disputeRef);

      if (!disputeDoc.exists()) {
        throw new Error('النزاع غير موجود');
      }

      const dispute = disputeDoc.data() as Dispute;
      const now = Date.now();

      const winningGuide = dispute.disputingGuides.find(
        (guide) => (guide.organizerId || guide.guideId) === resolvedOrganizerId
      );

      if (!winningGuide) {
        throw new Error('المنظم الفائز غير موجود');
      }

      const resolvedGuideId = winningGuide.guideId || '';

      const losingGuides = dispute.disputingGuides.filter(
        (guide) => (guide.organizerId || guide.guideId) !== resolvedOrganizerId
      );

      for (const losingGuide of losingGuides) {
        if (!losingGuide.guideId) {
          continue;
        }

        const banRef = doc(collection(firestore, COLLECTIONS.GUIDE_BANS));
        batch.set(banRef, {
          guideId: losingGuide.guideId,
          pilgrimId: dispute.pilgrimId,
          disputeId: disputeId,
          createdAt: now,
        });
      }

      // Update PilgrimGuideLinks
      const linksRef = collection(firestore, COLLECTIONS.LINKS);
      const linksQuery = query(
        linksRef,
        where('pilgrimId', '==', dispute.pilgrimId)
      );
      const linksSnapshot = await getDocs(linksQuery);

      linksSnapshot.forEach((doc) => {
        const linkData = doc.data();
        const isWinningLink = resolvedGuideId
          ? linkData.guideId === resolvedGuideId
          : linkData.organizerId === resolvedOrganizerId && !linkData.guideId;

        if (isWinningLink) {
          // Approve the winning guide's link
          batch.update(doc.ref, {
            status: resolvedGuideId ? 'approved' : 'pending',
            disputeId: null,
            updatedAt: now,
          });
        } else {
          // Delete other guides' links
          batch.delete(doc.ref);
        }
      });

      // Update ownership record
      const ownershipRef = collection(firestore, 'PilgrimOwnership');
      const ownershipQuery = query(
        ownershipRef,
        where('pilgrimId', '==', dispute.pilgrimId)
      );
      const ownershipSnapshot = await getDocs(ownershipQuery);

      if (resolvedGuideId && !ownershipSnapshot.empty) {
        // Update existing ownership record
        const ownershipDoc = ownershipSnapshot.docs[0];
        batch.update(ownershipDoc.ref, {
          realGuideId: resolvedGuideId,
          ownerGuideId: resolvedGuideId,
          updatedAt: now,
        });
      } else if (resolvedGuideId) {
        // Create new ownership record
        const newOwnershipRef = doc(collection(firestore, 'PilgrimOwnership'));
        batch.set(newOwnershipRef, {
          pilgrimId: dispute.pilgrimId,
          realGuideId: resolvedGuideId,
          ownerGuideId: resolvedGuideId,
          createdAt: now,
          updatedAt: now,
        });
      } else {
        ownershipSnapshot.forEach((ownershipDoc) => {
          batch.delete(ownershipDoc.ref);
        });
      }

      const disputeResolutionUpdate = {
        status: 'resolved',
        resolvedOrganizerId,
        resolvedAt: now,
        updatedAt: now,
        ...(resolvedGuideId ? { resolvedGuideId } : {}),
      };

      batch.update(disputeRef, disputeResolutionUpdate);

      await batch.commit();

      set((state) => {
        if (!state.disputes[disputeId]) {
          return state;
        }

        return {
          disputes: {
            ...state.disputes,
            [disputeId]: {
              ...state.disputes[disputeId],
              status: 'resolved',
              resolvedOrganizerId,
              resolvedAt: now,
              updatedAt: now,
              ...(resolvedGuideId ? { resolvedGuideId } : {}),
            },
          },
        };
      });
    } catch (error: any) {
      console.error('Error resolving dispute:', error);
      throw new Error(error.message || 'فشل في حل النزاع');
    }
  },

  reopenDispute: async (disputeId: string) => {
    try {
      const batch = writeBatch(firestore);
      const disputeRef = doc(firestore, COLLECTIONS.DISPUTES, disputeId);
      const disputeDoc = await getDoc(disputeRef);

      if (!disputeDoc.exists()) {
        throw new Error('النزاع غير موجود');
      }

      const dispute = disputeDoc.data() as Dispute;

      if (dispute.status !== 'resolved') {
        throw new Error('يمكن فقط إعادة فتح النزاعات المحلولة');
      }

      const now = Date.now();
      const nextStatus = inferReopenedStatus(dispute);

      const linksRef = collection(firestore, COLLECTIONS.LINKS);
      const guideBansRef = collection(firestore, COLLECTIONS.GUIDE_BANS);
      const ownershipRef = collection(firestore, 'PilgrimOwnership');

      const [pilgrimDoc, linksSnapshot, guideBansSnapshot, ownershipSnapshot] = await Promise.all([
        getDoc(doc(firestore, COLLECTIONS.PILGRIMS, dispute.pilgrimId)),
        getDocs(query(linksRef, where('pilgrimId', '==', dispute.pilgrimId))),
        getDocs(query(guideBansRef, where('disputeId', '==', disputeId))),
        getDocs(query(ownershipRef, where('pilgrimId', '==', dispute.pilgrimId))),
      ]);

      const pilgrimData = pilgrimDoc.exists() ? pilgrimDoc.data() : null;
      const guideCache = new Map<string, any>();
      const organizerCache = new Map<string, any>();
      const linksByParticipant = new Map<
        string,
        { ref: ReturnType<typeof doc>; data: Record<string, any> }
      >();

      linksSnapshot.docs.forEach((linkDoc) => {
        const linkData = linkDoc.data();
        const participantKey = linkData.guideId || linkData.organizerId;

        if (participantKey && !linksByParticipant.has(participantKey)) {
          linksByParticipant.set(participantKey, {
            ref: linkDoc.ref,
            data: linkData,
          });
        }
      });

      for (const participant of dispute.disputingGuides || []) {
        const participantKey = getParticipantKey(participant);
        if (!participantKey) {
          continue;
        }

        const existingLink = linksByParticipant.get(participantKey);
        if (existingLink) {
          const linkUpdate: Record<string, any> = {};

          if (existingLink.data.status !== 'disputed') {
            linkUpdate.status = 'disputed';
          }
          if (existingLink.data.disputeId !== disputeId) {
            linkUpdate.disputeId = disputeId;
          }

          if (Object.keys(linkUpdate).length > 0) {
            linkUpdate.updatedAt = now;
            batch.update(existingLink.ref, linkUpdate);
          }
          continue;
        }

        if (!pilgrimData) {
          continue;
        }

        let guideData: Record<string, any> | null = null;
        if (participant.guideId) {
          if (!guideCache.has(participant.guideId)) {
            const guideDoc = await getDoc(doc(firestore, COLLECTIONS.GUIDES, participant.guideId));
            guideCache.set(participant.guideId, guideDoc.exists() ? guideDoc.data() : null);
          }
          guideData = guideCache.get(participant.guideId) || null;
        }

        const organizerId = participant.organizerId || guideData?.organizerId || '';
        let organizerData: Record<string, any> | null = null;
        if (organizerId) {
          if (!organizerCache.has(organizerId)) {
            const organizerDoc = await getDoc(doc(firestore, COLLECTIONS.ORGANIZERS, organizerId));
            organizerCache.set(organizerId, organizerDoc.exists() ? organizerDoc.data() : null);
          }
          organizerData = organizerCache.get(organizerId) || null;
        }

        const linkRef = doc(collection(firestore, COLLECTIONS.LINKS));
        batch.set(linkRef, {
          id: linkRef.id,
          pilgrimId: dispute.pilgrimId,
          pilgrimName: pilgrimData.Name ?? dispute.pilgrimName ?? 'Unknown',
          passportNumber: pilgrimData.Passport_Number ?? dispute.passportNumber ?? 'Unknown',
          gender: pilgrimData.Gender?.toLowerCase() ?? 'unknown',
          residenceCountry: pilgrimData.Residence_Country ?? dispute.pilgrimCountry ?? null,
          packageName: pilgrimData.package_nickName ?? dispute.packageName ?? null,
          pilgrimType: pilgrimData.Pilgrim_Type ?? null,
          nusukGroup: pilgrimData.nusuk_group ?? null,
          email: pilgrimData.Email ? String(pilgrimData.Email).trim() : dispute.pilgrimEmail || null,
          mobileNumber: pilgrimData.Mobile_Number
            ? String(pilgrimData.Mobile_Number).trim()
            : dispute.pilgrimPhone || null,
          guideId: participant.guideId || '',
          guideName: guideData?.name ?? participant.guideName ?? 'Unassigned',
          organizerId,
          organizerName: organizerData?.name ?? participant.organizerName ?? 'Unknown Organizer',
          status: 'disputed',
          disputeId,
          createdAt: participant.submittedAt || now,
          updatedAt: now,
          isOwner: null,
        });
      }

      guideBansSnapshot.docs.forEach((guideBanDoc) => {
        batch.delete(guideBanDoc.ref);
      });

      ownershipSnapshot.docs.forEach((ownershipDoc) => {
        batch.delete(ownershipDoc.ref);
      });

      batch.update(disputeRef, {
        status: nextStatus,
        resolvedGuideId: null,
        resolvedOrganizerId: null,
        resolvedAt: null,
        updatedAt: now,
      });

      await batch.commit();

      set((state) => {
        if (!state.disputes[disputeId]) {
          return state;
        }

        return {
          disputes: {
            ...state.disputes,
            [disputeId]: {
              ...state.disputes[disputeId],
              status: nextStatus,
              resolvedGuideId: null,
              resolvedOrganizerId: null,
              resolvedAt: null,
              updatedAt: now,
            },
          },
        };
      });
    } catch (error: any) {
      console.error('Error reopening dispute:', error);
      throw new Error(error.message || 'فشل في إعادة فتح النزاع');
    }
  },
}));
